/** The Process Builder.
 *
 * Four steps, in the order a person actually thinks: name it, pick what it
 * watches, choose what to do, decide where the data goes. Infrastructure never
 * appears — no mention of workers, queues or containers.
 */

import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AppManifest, AppNode, ApiError, NormRect, ProcessGraph, Source, api,
} from "../lib/api";
import { useAsync } from "../lib/useLive";
import {
  Empty, ErrorBanner, FlowStrip, Loading, PageHeader, RegionEditor, SourceStatus,
} from "../components/ui";
import { Icon } from "../components/icons";

const STEPS = ["Details", "Source", "Apps", "Data & storage"];

export function ProcessBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);

  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [nodes, setNodes] = useState<AppNode[]>([]);
  const [runtime, setRuntime] = useState<Record<string, unknown>>({ preview_fps: 12 });
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const sources = useAsync<Source[]>(() => api.sources(), []);
  const catalog = useAsync<AppManifest[]>(() => api.apps(), []);

  useAsync(async () => {
    if (!id) return null;
    const process = await api.process(id);
    setName(process.name);
    setDescription(process.description);
    setTags((process.tags ?? []).join(", "));
    setSourceId(process.graph.source.source_id);
    setNodes(process.graph.nodes);
    setRuntime({ ...process.graph.runtime, preview_fps: 12 });
    return process;
  }, [id]);

  const source = sources.data?.find((candidate) => candidate.id === sourceId);
  const manifests = catalog.data ?? [];
  const manifestFor = (appId: string) => manifests.find((m) => m.app_id === appId);

  const graph: ProcessGraph = useMemo(() => ({
    name: name || "Untitled process",
    description,
    tags: tags.split(",").map((tag) => tag.trim()).filter(Boolean),
    source: { source_id: sourceId },
    nodes,
    connections: buildConnections(nodes, manifests),
    runtime,
  }), [name, description, tags, sourceId, nodes, runtime, manifests]);

  const addApp = (manifest: AppManifest) => {
    const base = manifest.app_id;
    let nodeId = base;
    let suffix = 2;
    while (nodes.some((node) => node.node_id === nodeId)) nodeId = `${base}_${suffix++}`;

    const params: Record<string, unknown> = {};
    manifest.params.forEach((spec) => {
      if (spec.default !== null && spec.default !== undefined && spec.type !== "regions" && spec.type !== "lines") {
        params[spec.key] = spec.default;
      }
    });

    setNodes([...nodes, {
      node_id: nodeId,
      app_id: manifest.app_id,
      app_version: manifest.version,
      processing_fps: manifest.default_processing_fps || null,
      params,
      regions: [],
      region_names: [],
      sinks: defaultSinks(manifest),
    }]);
    setSelected(nodeId);
  };

  const patch = (nodeId: string, changes: Partial<AppNode>) =>
    setNodes(nodes.map((node) => (node.node_id === nodeId ? { ...node, ...changes } : node)));

  const save = async (startAfter: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const payload = { name: graph.name, description, tags: graph.tags, graph };
      const process = editing
        ? await api.updateProcess(id!, { ...payload, note: "edited in builder" })
        : await api.createProcess(payload);
      if (startAfter) {
        const run = await api.start(process.id);
        navigate(`/runs/${run.id}`);
      } else {
        navigate(`/processes/${process.id}`);
      }
    } catch (err) {
      const problems = err instanceof ApiError && err.problems
        ? err.problems.map((problem) => `${problem.field}: ${problem.message}`).join("; ")
        : "";
      setError(`${(err as Error).message}${problems ? ` — ${problems}` : ""}`);
      setSaving(false);
    }
  };

  const canAdvance = [Boolean(name.trim()), Boolean(sourceId), nodes.length > 0, true][step];
  const selectedNode = nodes.find((node) => node.node_id === selected);

  return (
    <div className="page stack">
      <PageHeader
        title={editing ? `Edit ${name || "process"}` : "New process"}
        description={
          editing
            ? "Saving creates a new version. Anything running now keeps the configuration it started with."
            : "Point a HoloPod at one or more apps and choose where the results are stored."
        }
      />

      <div className="builder-nav" role="navigation" aria-label="Process builder">
        <div className="steps">
          {STEPS.map((label, index) => (
            <button
              key={label}
              className={`step${index === step ? " active" : index < step ? " done" : ""}`}
              onClick={() => setStep(index)}
            >
              <span className="n">{index + 1}</span>
              {label}
            </button>
          ))}
        </div>
        <div className="builder-actions">
          <span className="hint">Step {step + 1} of {STEPS.length}</span>
          <button className="btn" disabled={step === 0} onClick={() => setStep(step - 1)}>Back</button>
          {step < STEPS.length - 1 ? (
            <button className="btn primary" disabled={!canAdvance} onClick={() => setStep(step + 1)}>
              Continue
              <Icon name="arrowRight" size={14} />
            </button>
          ) : (
            <>
              <button className="btn" disabled={saving} onClick={() => save(false)}>Save</button>
              <button className="btn primary" disabled={saving} onClick={() => save(true)}>
                {saving ? "Starting…" : "Save and start"}
              </button>
            </>
          )}
        </div>
      </div>

      {error && <ErrorBanner error={error} />}

      {nodes.length > 0 && sourceId && (
        <div className="card">
          <FlowStrip
            graph={graph}
            apps={manifests}
            sourceName={source?.name}
            onSelect={(nodeId) => { setSelected(nodeId); setStep(2); }}
            selected={selected ?? undefined}
          />
        </div>
      )}

      {step === 0 && (
        <div className="card stack" style={{ maxWidth: 620 }}>
          <label className="field">
            Process name
            <input
              type="text" value={name} placeholder="OR Monitor OCR"
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          <label className="field">
            Description
            <textarea
              value={description} placeholder="What this process is for and who relies on it"
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>
          <label className="field">
            Tags
            <input
              type="text" value={tags} placeholder="or-01, cardiology, study-a"
              onChange={(event) => setTags(event.target.value)}
            />
            <span className="hint">Comma separated. Used for filtering later.</span>
          </label>
        </div>
      )}

      {step === 1 && (
        <div className="stack">
          {sources.loading && <Loading rows={3} />}
          {sources.data?.length === 0 && (
            <Empty
              title="No HoloPods registered"
              body="Add a pod or another video source first, then come back to this process."
              action={<Link className="btn primary" to="/holopods">Go to HoloPods</Link>}
            />
          )}
          <div className="grid cols-3">
            {(sources.data ?? []).map((candidate) => (
              <div
                key={candidate.id}
                className={`card interactive stack${candidate.id === sourceId ? " selected" : ""}`}
                onClick={() => setSourceId(candidate.id)}
              >
                <div className="row">
                  <SourceStatus status={candidate.status} />
                  <div className="spacer" />
                  <span className="tag">{candidate.kind}</span>
                </div>
                <div>
                  <div className="card-title">{candidate.name}</div>
                  <div className="hint">{candidate.location || candidate.description || "—"}</div>
                </div>
                <div className="mono" style={{ fontSize: 11, color: "var(--text-faint)" }}>
                  {candidate.width && candidate.height ? `${candidate.width}×${candidate.height}` : "resolution unknown"}
                  {candidate.fps ? ` · ${candidate.fps} fps` : ""}
                  {candidate.active_runs ? ` · ${candidate.active_runs} process(es) using it` : ""}
                </div>
                {candidate.id === sourceId && candidate.kind !== "holopod" && (
                  <div className="preview" style={{ marginTop: 4 }}>
                    <img src={api.previewUrl(candidate.id)} alt={`${candidate.name} preview`} />
                  </div>
                )}
              </div>
            ))}
          </div>
          {source && source.active_runs > 0 && (
            <div className="notice">
              {source.active_runs} process(es) already read from this pod. HoloXI shares one decoded
              stream between them rather than opening it again.
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="grid split">
          <div className="stack">
            <h2>Add an app</h2>
            {catalog.loading && <Loading rows={3} />}
            <div className="grid cols-2">
              {manifests.map((manifest) => (
                <div key={manifest.app_id} className="card stack" style={{ gap: 10 }}>
                  <div className="row">
                    <div className="card-title">{manifest.name}</div>
                    <div className="spacer" />
                    {!manifest.available && <span className="badge failed">unavailable</span>}
                    {manifest.compute !== "cpu" && <span className="tag">{manifest.compute.replace("_", " ")}</span>}
                  </div>
                  <p className="hint" style={{ margin: 0 }}>{manifest.description}</p>
                  {!manifest.available && (
                    <p className="hint" style={{ color: "var(--coral)", margin: 0 }}>
                      Needs {manifest.missing_requirements.join(", ")} on a worker.
                    </p>
                  )}
                  {manifest.consumes.length > 0 && (
                    <p className="hint" style={{ margin: 0 }}>
                      Reads {manifest.consumes.join(", ")} from an app above it.
                    </p>
                  )}
                    <button
                      className="btn sm"
                      disabled={!manifest.available}
                      onClick={() => addApp(manifest)}
                    >
                      <Icon name="plus" size={13} />
                      Add app
                    </button>
                </div>
              ))}
            </div>
          </div>

          <div className="stack">
            <h2>Configure</h2>
            {!selectedNode && (
              <div className="card">
                <p className="hint" style={{ margin: 0 }}>
                  {nodes.length === 0
                    ? "Add an app to configure it."
                    : "Select an app in the pipeline above to configure it."}
                </p>
              </div>
            )}
            {selectedNode && (
              <NodeConfig
                node={selectedNode}
                manifest={manifestFor(selectedNode.app_id)!}
                source={source}
                onChange={(changes) => patch(selectedNode.node_id, changes)}
                onRemove={() => {
                  setNodes(nodes.filter((node) => node.node_id !== selectedNode.node_id));
                  setSelected(null);
                }}
              />
            )}
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="stack">
          <h2>Where the data goes</h2>
          <p className="hint" style={{ maxWidth: "62ch", marginTop: -8 }}>
            Turn structured outputs on or off for each app. Media files (images, video, masks)
            are written by the app itself when it produces them — control that with the options
            below, not as a separate sink.
          </p>
          {nodes.map((node) => {
            const manifest = manifestFor(node.app_id);
            if (!manifest) return null;
            const structured = manifest.storage_capabilities.filter((kind) => STRUCTURED_KINDS.has(kind));
            const media = manifest.storage_capabilities.filter((kind) => MEDIA_KINDS.has(kind));
            const mediaControls = mediaControlParams(manifest);
            return (
              <div className="card stack" key={node.node_id}>
                <div className="row">
                  <div className="card-title">{manifest.name}</div>
                  <div className="spacer" />
                  <span className="id">{node.node_id}</span>
                </div>

                {structured.length > 0 && (
                  <div className="stack" style={{ gap: 8 }}>
                    <div className="stat-label">Structured results</div>
                    <div className="row wrap" style={{ gap: 16 }}>
                      {structured.map((kind) => {
                        const enabled = node.sinks.some((sink) => sink.kind === kind);
                        return (
                          <label className="row" key={kind} style={{ gap: 7 }}>
                            <input
                              type="checkbox"
                              checked={enabled}
                              onChange={(event) =>
                                patch(node.node_id, {
                                  sinks: event.target.checked
                                    ? [...node.sinks, {
                                        kind: kind as AppNode["sinks"][number]["kind"],
                                        rotation: kind === "csv" ? "rows" : "none",
                                        rotate_rows: 100000,
                                        consolidate_on_stop: true,
                                      }]
                                    : node.sinks.filter((sink) => sink.kind !== kind),
                                })
                              }
                            />
                            <span style={{ fontSize: 13 }}>{LABELS[kind] ?? kind}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {node.sinks.some((sink) => sink.kind === "csv") && (
                  <div className="row wrap" style={{ gap: 12 }}>
                    <label className="field" style={{ maxWidth: 200 }}>
                      Split CSV files
                      <select
                        value={node.sinks.find((sink) => sink.kind === "csv")?.rotation ?? "none"}
                        onChange={(event) =>
                          patch(node.node_id, {
                            sinks: node.sinks.map((sink) =>
                              sink.kind === "csv" ? { ...sink, rotation: event.target.value as any } : sink),
                          })
                        }
                      >
                        <option value="none">Never</option>
                        <option value="rows">Every 100,000 rows</option>
                        <option value="time">Every 10 minutes</option>
                        <option value="size">Every 256 MB</option>
                      </select>
                    </label>
                    <label className="row" style={{ gap: 7, alignSelf: "flex-end", paddingBottom: 8 }}>
                      <input
                        type="checkbox"
                        checked={node.sinks.find((sink) => sink.kind === "csv")?.consolidate_on_stop ?? true}
                        onChange={(event) =>
                          patch(node.node_id, {
                            sinks: node.sinks.map((sink) =>
                              sink.kind === "csv" ? { ...sink, consolidate_on_stop: event.target.checked } : sink),
                          })
                        }
                      />
                      <span style={{ fontSize: 13 }}>Also produce one combined CSV when it stops</span>
                    </label>
                  </div>
                )}

                {media.length > 0 && (
                  <div className="stack" style={{ gap: 8 }}>
                    <div className="stat-label">Media files</div>
                    <div className="row wrap" style={{ gap: 6 }}>
                      {media.map((kind) => (
                        <span className="tag media-cap" key={kind}>{LABELS[kind] ?? kind}</span>
                      ))}
                    </div>
                    {mediaControls.length > 0 ? (
                      <div className="stack" style={{ gap: 8 }}>
                        {mediaControls.map((spec) => (
                          <label className="row" key={spec.key} style={{ gap: 8, alignItems: "flex-start" }}>
                            <input
                              type="checkbox"
                              style={{ marginTop: 2 }}
                              checked={Boolean(node.params[spec.key] ?? spec.default)}
                              onChange={(event) =>
                                patch(node.node_id, {
                                  params: { ...node.params, [spec.key]: event.target.checked },
                                })
                              }
                            />
                            <span style={{ fontSize: 13 }}>
                              {spec.label}
                              {spec.help && (
                                <span className="hint" style={{ display: "block" }}>{spec.help}</span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="hint" style={{ margin: 0 }}>
                        {inherentMediaNote(manifest)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}

const LABELS: Record<string, string> = {
  csv: "CSV", jsonl: "JSONL", database: "Database (searchable)",
  image: "Images", video: "Video", mask: "Masks",
};

const STRUCTURED_KINDS = new Set(["csv", "jsonl", "database"]);
const MEDIA_KINDS = new Set(["image", "video", "mask"]);

/** Bool params that actually gate writing media artifacts. */
function mediaControlParams(manifest: AppManifest) {
  return manifest.params.filter((spec) =>
    spec.type === "bool" && /^(snapshot_|save_mask|save_overlay|save_image)/.test(spec.key));
}

function inherentMediaNote(manifest: AppManifest): string {
  const media = manifest.storage_capabilities.filter((kind) => MEDIA_KINDS.has(kind));
  const labels = media.map((kind) => (LABELS[kind] ?? kind).toLowerCase());
  const list = labels.length <= 1
    ? labels[0]
    : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;

  if (["raw_recorder", "snapshot", "dataset_collector"].includes(manifest.app_id)) {
    return `This app’s job is to write ${list} to object storage while it runs.`;
  }
  return `${list?.charAt(0).toUpperCase()}${list?.slice(1)} land in object storage only when this app produces them — there is no separate “always on” sink.`;
}

function defaultSinks(manifest: AppManifest): AppNode["sinks"] {
  if (manifest.app_id === "raw_recorder") return [];
  return [
    { kind: "csv", rotation: "rows", rotate_rows: 100000, consolidate_on_stop: true },
    { kind: "database" },
  ];
}

/** Wire each app to the nearest upstream node that produces what it consumes. */
function buildConnections(nodes: AppNode[], manifests: AppManifest[]) {
  const byId = new Map(manifests.map((manifest) => [manifest.app_id, manifest]));
  return nodes.flatMap((node, index) => {
    const manifest = byId.get(node.app_id);
    if (!manifest || manifest.consumes.length === 0) {
      return [{ from_node: "source", to_node: node.node_id }];
    }
    for (let i = index - 1; i >= 0; i -= 1) {
      const upstream = byId.get(nodes[i].app_id);
      if (upstream?.produces.some((channel) => manifest.consumes.includes(channel))) {
        return [{ from_node: nodes[i].node_id, to_node: node.node_id }];
      }
    }
    return [{ from_node: "source", to_node: node.node_id }];
  });
}

function NodeConfig({ node, manifest, source, onChange, onRemove }: {
  node: AppNode;
  manifest: AppManifest;
  source?: Source;
  onChange: (changes: Partial<AppNode>) => void;
  onRemove: () => void;
}) {
  const [advanced, setAdvanced] = useState(false);
  const models = useAsync(() => api.models(), []);
  const needsRegions = manifest.params.some((spec) => spec.type === "regions");

  const setParam = (key: string, value: unknown) =>
    onChange({ params: { ...node.params, [key]: value } });

  return (
    <div className="card stack">
      <div className="row">
        <div className="card-title">{manifest.name}</div>
        <div className="spacer" />
        <button className="btn ghost sm" onClick={onRemove}>Remove</button>
      </div>

      <label className="field">
        Runs at
        <div className="row">
          <input
            type="range" min={0} max={30} step={0.5}
            value={node.processing_fps ?? 0}
            onChange={(event) => onChange({ processing_fps: Number(event.target.value) || null })}
          />
          <span className="mono" style={{ width: 92, textAlign: "right" }}>
            {node.processing_fps ? `${node.processing_fps} fps` : "every frame"}
          </span>
        </div>
        <span className="hint">
          Lower rates cost less compute. The stream keeps its own frame rate regardless.
        </span>
      </label>

      {manifest.requires_model && (
        <label className="field">
          Model
          <select
            value={node.model_id ?? ""}
            onChange={(event) => onChange({ model_id: event.target.value || null })}
          >
            <option value="">Select a model…</option>
            {(models.data ?? []).map((model) => (
              <option key={model.id} value={model.id}>{model.name} · v{model.latest_version}</option>
            ))}
          </select>
          {(models.data ?? []).length === 0 && (
            <span className="hint" style={{ color: "var(--amber)" }}>
              No models registered yet. This app cannot run until one is.
            </span>
          )}
        </label>
      )}

      {needsRegions && source && (
        <div className="stack" style={{ gap: 8 }}>
          <div className="stat-label">Regions</div>
          <RegionEditor
            imageUrl={api.previewUrl(source.id)}
            regions={node.regions}
            names={node.region_names}
            onChange={(regions, names) => onChange({ regions, region_names: names })}
            aspectLabel={source.name}
          />
          {node.region_names.length > 0 && (
            <div className="card flush">
              <table>
                <thead><tr><th>Region</th><th>Expected value</th></tr></thead>
                <tbody>
                  {node.region_names.map((name, index) => {
                    const regionName = name || `region_${index + 1}`;
                    const regionOptions = (
                      (node.params.region_options as Record<string, Record<string, unknown>> | undefined) ?? {}
                    );
                    return (
                      <tr key={`${regionName}-${index}`}>
                        <td>{regionName}</td>
                        <td>
                          <select
                            value={String(regionOptions[regionName]?.expected_type ?? "text")}
                            onChange={(event) => setParam("region_options", {
                              ...regionOptions,
                              [regionName]: {
                                ...(regionOptions[regionName] ?? {}),
                                expected_type: event.target.value,
                              },
                            })}
                          >
                            <option value="text">Free text</option>
                            <option value="integer">Integer</option>
                            <option value="decimal">Decimal</option>
                            <option value="percentage">Percentage</option>
                            <option value="datetime">Date / time</option>
                            <option value="alphanumeric">Alphanumeric</option>
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {manifest.params
        .filter((spec) => !["regions", "lines", "model_ref"].includes(spec.type))
        .filter((spec) => !mediaControlParams(manifest).some((media) => media.key === spec.key))
        .filter((spec) => advanced || !spec.advanced)
        .map((spec) => (
          <label className="field" key={spec.key}>
            {spec.label}
            {spec.type === "enum" ? (
              <select
                value={String(node.params[spec.key] ?? spec.default ?? "")}
                onChange={(event) => setParam(spec.key, event.target.value)}
              >
                {(spec.options ?? []).map((option) => (
                  <option key={option} value={option}>{option.replace(/_/g, " ")}</option>
                ))}
              </select>
            ) : spec.type === "bool" ? (
              <div className="row" style={{ gap: 8 }}>
                <input
                  type="checkbox"
                  checked={Boolean(node.params[spec.key] ?? spec.default)}
                  onChange={(event) => setParam(spec.key, event.target.checked)}
                />
                <span className="hint">{spec.help}</span>
              </div>
            ) : (
              <input
                type={spec.type === "string" ? "text" : "number"}
                value={String(node.params[spec.key] ?? spec.default ?? "")}
                min={spec.min} max={spec.max}
                step={spec.type === "float" ? 0.01 : 1}
                onChange={(event) =>
                  setParam(spec.key, spec.type === "string" ? event.target.value : Number(event.target.value))
                }
              />
            )}
            {spec.help && spec.type !== "bool" && <span className="hint">{spec.help}</span>}
          </label>
        ))}

      {manifest.params.some((spec) => spec.advanced) && (
        <button className="btn ghost sm" onClick={() => setAdvanced(!advanced)}>
          {advanced ? "Hide advanced settings" : "Show advanced settings"}
        </button>
      )}

      <details>
        <summary className="hint" style={{ cursor: "pointer" }}>What this app records</summary>
        <div className="card flush" style={{ marginTop: 8 }}>
          <table>
            <tbody>
              {manifest.output_schema.map((field) => (
                <tr key={field.name}>
                  <td className="mono">{field.name}</td>
                  <td className="hint">{field.type}</td>
                  <td className="hint">{field.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
